/**
 * Links the user's login connection to the Auth0 Token Vault, so the server can
 * refresh their identity provider token later instead of reusing the one
 * captured at login.
 *
 * The server asks for this (`token_vault_seed` on the token mint response) when
 * the organization has refresh enabled and Auth0 holds no refresh token for the
 * user yet. The Auth0 SDK runs the Connected Accounts handshake itself: it mints
 * a My Account API token, asks Auth0 for a one-time ticket, sends the browser to
 * the identity provider, and on the way back redeems the code inside
 * `handleRedirectCallback`, bound to this tab with PKCE. What is left here is
 * ours alone: deciding whether to attempt a link, telling the way back from a
 * failed login, and counting how far each attempt got.
 *
 * With a live session at the identity provider the round trip is two silent
 * redirects; Google additionally shows an account picker and one consent screen.
 * Anything that goes wrong leaves the user logged in on the login-time token,
 * which is how Superblocks behaves without this feature at all.
 */

import {
  ConnectError,
  GenericError,
  MyAccountApiError,
  type Auth0Client,
  type GetTokenSilentlyOptions,
} from "@auth0/auth0-spa-js";

import type { TokenVaultSeedRequest } from "@superblocksteam/shared";

import { uiLogger } from "core/telemetry/logger";
import envs from "env";

import { recordSeedStep } from "./tokenVaultSeedMetrics";

/**
 * Set once this tab can no longer be sure the browser has not been handed to the
 * identity provider, and never cleared, so a trip that comes back without a code
 * cannot turn into a redirect loop. A new tab tries again.
 *
 * That is the moment of navigation, and also the moment the caller stops
 * waiting: the SDK cannot be cancelled, and a handshake that outlives the wait
 * still writes Auth0's single per-tab transaction record when it finally
 * arrives. A second handshake started in between would have its own record
 * overwritten by that late one, and the callback would then fail as
 * `state_mismatch`.
 *
 * Deliberately not set for a failure that happened before either point: a token
 * Auth0 would not mint, or a handshake it would not start, navigated nowhere and
 * is safe to retry on the next mint.
 */
const ATTEMPTED_KEY = "superblocks.tokenVaultSeed.attempted";
/**
 * Where the user was when the browser left, and which connection it left to
 * link. Written at the moment of navigation and consumed by whichever callback
 * brings the browser back, so while it exists this tab is mid-handshake.
 *
 * Two things read it. An Auth0 error that arrives while it exists belongs to the
 * link, not to a login, whatever class the SDK chose for it. And the SDK drops
 * its own record of the handshake before it redeems the code, so when Auth0 then
 * refuses to finish the link, the state that travelled with the handshake is
 * already gone and this is the only copy left.
 */
const PENDING_KEY = "superblocks.tokenVaultSeed.pending";

/**
 * The error codes OAuth 2.0 and OpenID Connect define for an authorize response.
 * Whatever comes back on the callback is whatever the upstream provider chose to
 * send, so anything outside this list is counted as one bucket rather than
 * minting a new metric series per string a provider invents.
 */
const OAUTH_ERRORS = new Set([
  "access_denied",
  "account_selection_required",
  "consent_required",
  "interaction_required",
  "invalid_request",
  "invalid_scope",
  "login_required",
  "server_error",
  "temporarily_unavailable",
  "unauthorized_client",
  "unsupported_response_type",
]);

function knownOauthError(code: string): string {
  return OAUTH_ERRORS.has(code) ? code : "other";
}
/**
 * Auth0's answers when it will not authorize silently, which for us both mean the
 * session has aged past the window right after a login.
 *
 * `unmet_authentication_requirements` is the one seen in practice, and its
 * description says why the silent path cannot recover: "cannot be challenged".
 * Raising the session's assurance needs a prompt, and a silent authorize forbids
 * prompting by definition, so this answer does not change until the next login.
 *
 * The SDK mints the My Account token through that same silent authorize, so this
 * stays true until multi-resource refresh tokens are switched on for the client.
 */
const SESSION_TOO_OLD = new Set([
  "login_required",
  "unmet_authentication_requirements",
]);
/**
 * The caller holds the login spinner while this runs, so the whole thing has to
 * be bounded. Auth0 degrading into slow-but-not-failing is the case this covers:
 * without it a hung silent authorize or a hung ticket request would hold the
 * spinner indefinitely and produce no signal at all. The SDK's own timeout on the
 * silent authorize is a minute, which is far longer than a spinner can be held.
 */
const HANDSHAKE_TIMEOUT_MS = 5_000;
/**
 * A re-link first removes the account Auth0 already holds for the connection,
 * because the identity provider no longer accepts its refresh token and Auth0
 * does not say whether a second link replaces the first or sits beside it.
 * Removing it makes the link that follows a first link, the one path with
 * production evidence. Its own budget, so a slow clean-up cannot eat the
 * handshake's. Every failure here is logged, counted and then ignored: the link
 * goes ahead exactly as it would have without the clean-up.
 */
const UNLINK_TIMEOUT_MS = 5_000;
const MY_ACCOUNT_SCOPES =
  "read:me:connected_accounts delete:me:connected_accounts";

type GetAccessToken = (
  options: GetTokenSilentlyOptions,
) => Promise<string | undefined>;
type ConnectedAccount = { connection: string; id: string };

/**
 * Travels with the SDK's transaction record and comes back on the callback, on
 * the result when the link is made and on the error when it is refused. Also
 * what the per-tab record above holds.
 */
export type TokenVaultSeedAppState = {
  /** Where the user was when the handshake started. */
  returnUrl: string;
  /** Which link this was, for the log lines on the way back. */
  tokenVaultSeed: TokenVaultSeedRequest;
};

/**
 * One attempt at a time across the whole page. `ATTEMPTED_KEY` is only written
 * once Auth0 has issued a ticket, so two mints that overlap - a view mode
 * change, a token refetch, the app and dashboard trees both mounting - would
 * each start a handshake of their own and each try to navigate.
 */
let seedInFlight: Promise<void> | undefined;

/**
 * Runs on its own while the page loads, so it can only take the token Auth0 will
 * hand over without involving the user. That works while the session is new, which
 * is the pass right after a login, and Auth0 declines once the session has aged.
 * Declining is fine: the user keeps the token they logged in with, and the next
 * login tries again.
 *
 * `connectAccount` is the SDK's `connectAccountWithRedirect`, as `useAuth0()`
 * hands it out.
 */
export async function startTokenVaultSeed({
  connectAccount,
  getAccessToken,
  seed,
}: {
  connectAccount: Auth0Client["connectAccountWithRedirect"];
  /** `getAccessTokenSilently` from `useAuth0()`; mints the My Account token a re-link needs. */
  getAccessToken?: GetAccessToken;
  seed: TokenVaultSeedRequest;
}): Promise<void> {
  recordSeedStep("requested", { reason: seed.reason });
  // Embedded Superblocks lives in someone else's page; never navigate it away.
  if (window.self !== window.top) {
    recordSeedStep("skipped", { reason: "embedded" });
    return;
  }
  if (window.sessionStorage.getItem(ATTEMPTED_KEY)) {
    recordSeedStep("skipped", { reason: "already_attempted" });
    return;
  }
  if (seedInFlight) {
    // A second caller for the same handshake rather than a second handshake. Both
    // `useJwt` trees can mount, Strict Mode double-invokes, and a view-mode
    // remint calls in again. Counted, because `requested` is recorded per call
    // and an uncounted coalesce reads as a drop between `requested` and
    // `started` - which is the one thing this funnel exists to measure.
    recordSeedStep("skipped", { reason: "already_in_flight" });
    await seedInFlight;
    return;
  }
  seedInFlight = runSeed({ connectAccount, getAccessToken, seed }).finally(
    () => {
      seedInFlight = undefined;
    },
  );
  await seedInFlight;
}

/**
 * Call on the way out. The markers above guard one session's attempt at linking,
 * and logging out ends that session: the next person to sign in, or the same
 * person signing in again, deserves a fresh attempt rather than inheriting a
 * marker set before they arrived.
 */
export function forgetTokenVaultSeedAttempt(): void {
  window.sessionStorage.removeItem(ATTEMPTED_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);
}

/**
 * True when the current Auth0 result belongs to the pending link. A successful
 * return has a `connect_code`; a refusal has no link-specific query parameter,
 * but the SDK identifies it with `ConnectError`. Completion failures can have
 * other error classes, so the code in the URL is the evidence for those.
 *
 * A pending record without either signal is stale. Clear it before a later
 * login error can be mistaken for a failed link, while keeping `ATTEMPTED_KEY`
 * so this tab cannot enter a redirect loop.
 */
export function isTokenVaultSeedCallback(auth0Error?: Error): boolean {
  if (window.sessionStorage.getItem(PENDING_KEY) === null) {
    return false;
  }
  if (
    auth0Error instanceof ConnectError ||
    new URLSearchParams(window.location.search).has("connect_code")
  ) {
    return true;
  }
  window.sessionStorage.removeItem(PENDING_KEY);
  return false;
}

async function runSeed({
  connectAccount,
  getAccessToken,
  seed,
}: {
  connectAccount: Auth0Client["connectAccountWithRedirect"];
  getAccessToken?: GetAccessToken;
  seed: TokenVaultSeedRequest;
}): Promise<void> {
  if (seed.reason === "relink" && getAccessToken) {
    await removeDeadLink(seed.connection, getAccessToken);
  }
  const deadline = Date.now() + HANDSHAKE_TIMEOUT_MS;
  const appState: TokenVaultSeedAppState = {
    returnUrl: window.location.href,
    tokenVaultSeed: seed,
  };
  try {
    await withTimeout(
      // No `scopes`: omitting it leaves Auth0 using each connection's own
      // configured scopes, which is the only thing that is right for every
      // provider at once. Okta only returns a refresh token when `offline_access`
      // is asked for; Google refuses that scope outright. Both facts already live
      // on the connection, and nothing in a mint request reliably says which
      // provider is upstream - a connection named anything at all can federate
      // to Google.
      connectAccount({
        appState,
        authorization_params: { login_hint: seed.login_hint },
        connection: seed.connection,
        openUrl: async (url) => {
          // The SDK cannot be cancelled, so a handshake that outlived the
          // caller's patience can still get this far. By then the spinner is
          // gone and the app is usable; navigating now would read as the page
          // redirecting itself for no reason.
          if (Date.now() >= deadline) {
            // Counted apart from the timeout above: that one says how often
            // Auth0 was too slow for the budget, this one says how many of
            // those would have worked had the budget been longer.
            recordSeedStep("abandoned");
            uiLogger.warn(
              "Skipped a Token Vault link that was ready too late to use",
              { "vault.connection": seed.connection },
            );
            return;
          }
          // Set here, not before the attempt: this is the point where we hand
          // the browser to the provider and stop being able to see what happens.
          // If it comes back without a code the server still reports an empty
          // vault, quite correctly, and asks again - so without this the pair
          // would bounce forever. Everything before this line either failed
          // without navigating or succeeded, and both are safe to try again on
          // the next mint.
          window.sessionStorage.setItem(ATTEMPTED_KEY, "true");
          window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(appState));
          recordSeedStep("started");
          const destination = new URL(url);
          uiLogger.info(
            "Sending the browser to link the Token Vault connection",
            {
              "vault.connection": seed.connection,
              // Where Auth0 said to send them, without the ticket, which is a
              // one-time credential. When a link fails the browser never comes
              // back, so this is the last thing we know about it: a host that is
              // not the identity provider's says the handshake went wrong before
              // the user ever saw a consent screen.
              "vault.seed.destination": `${destination.origin}${destination.pathname}`,
            },
          );
          window.location.assign(url);
        },
      }),
      deadline,
    );
  } catch (error) {
    const oauthCode = readOauthCode(error);
    // A session Auth0 will not authorize silently does not become authorizable
    // again without a new login, and the server keeps asking on every mint, so
    // without this a deployed app re-minting every few minutes would attempt a
    // hidden authorize every few minutes for the whole session, forever. A
    // handshake still running when the wait ran out may yet write its
    // transaction record, so nothing else may start one in this tab either.
    // Any other failure is worth retrying on the next mint.
    if (
      error instanceof HandshakeTimeout ||
      (oauthCode !== undefined && SESSION_TOO_OLD.has(oauthCode))
    ) {
      window.sessionStorage.setItem(ATTEMPTED_KEY, "true");
    }
    // Auth0 refusing the token and Auth0 refusing the handshake are different
    // problems with different owners, and the funnel is only useful if it tells
    // them apart.
    recordSeedStep(oauthCode ? "token_refused" : "connect_refused", {
      reason: oauthCode ?? readMyAccountFailure(error),
    });
    uiLogger.warn("Could not start linking the Token Vault connection", {
      "error.message": String(error),
      "vault.connection": seed.connection,
      ...(oauthCode ? { "vault.seed.error_code": oauthCode } : {}),
    });
  }
}

/**
 * The browser is back and Auth0 has vaulted the refresh token. Called with the
 * `appState` the SDK's `handleRedirectCallback` returned.
 */
export function onTokenVaultSeedLinked(
  appState: Partial<TokenVaultSeedAppState>,
): void {
  takePendingAppState();
  recordSeedStep("linked");
  uiLogger.info("Linked the Token Vault connection", {
    "vault.connection": appState.tokenVaultSeed?.connection,
  });
  // Reloading also drops `connect_code` from the address bar and mints a token
  // that carries the now-refreshable identity provider token.
  window.location.replace(sameOriginUrl(appState.returnUrl));
}

/**
 * The browser is back without a link. Called with whatever the SDK's
 * `handleRedirectCallback` threw while `isTokenVaultSeedCallback()` held.
 *
 * Whatever it was, it is not a failed login. The session is as good as it was a
 * minute ago, so the user goes back to what they were doing rather than to an
 * error page.
 *
 * A refusal is not remembered beyond this tab. The SDK raises a `ConnectError`
 * before it checks the `state` the browser came back with, so a callback
 * carrying `error=access_denied` and someone else's state would read as the
 * user's own answer, and a refusal written to local storage on that evidence
 * would keep them out of seeding until they cleared browser storage by hand.
 * The per-tab marker still stops the loop; a new tab asks once more.
 */
export function onTokenVaultSeedRefused(error: unknown): void {
  const pending = takePendingAppState();
  const connection =
    pending.tokenVaultSeed?.connection ??
    (error instanceof ConnectError ? error.connection : undefined);
  if (error instanceof ConnectError) {
    recordSeedStep("refused", { reason: knownOauthError(error.error) });
    uiLogger.warn("Linking the Token Vault connection was refused", {
      "vault.connection": connection,
      // The code as sent, unlike the metric label: a log line takes an
      // unfamiliar one without cost, and it is the only place the upstream
      // provider's own wording survives. The description can carry account
      // details, so it stays out.
      "vault.seed.reason": error.error,
    });
  } else {
    recordSeedStep("complete_refused", {
      reason: readCompletionFailure(error),
    });
    uiLogger.warn("Could not finish linking the Token Vault connection", {
      "error.message": String(error),
      "vault.connection": connection,
    });
  }
  window.location.replace(
    sameOriginUrl(pending.returnUrl ?? readAppState(error).returnUrl),
  );
}

async function removeDeadLink(
  connection: string,
  getAccessToken: GetAccessToken,
): Promise<void> {
  const deadline = Date.now() + UNLINK_TIMEOUT_MS;
  let apiBase: string;
  let token: string;
  try {
    apiBase = myAccountApiUrl();
    const minted = await withTimeout(
      getAccessToken({
        authorizationParams: { audience: apiBase, scope: MY_ACCOUNT_SCOPES },
      }),
      deadline,
    );
    if (typeof minted !== "string" || minted.length === 0) {
      throw new Error("Auth0 minted no My Account token");
    }
    token = minted;
  } catch (error) {
    recordSeedStep("unlink_refused", {
      reason:
        error instanceof HandshakeTimeout
          ? "timeout"
          : (readOauthCode(error) ?? "token_refused"),
    });
    uiLogger.warn(
      "Could not remove the dead Token Vault link; linking anyway",
      {
        "error.message": String(error),
        "vault.connection": connection,
      },
    );
    return;
  }
  try {
    const accounts = await myAccountRequest<{ accounts?: ConnectedAccount[] }>(
      `${apiBase}v1/connected-accounts/accounts`,
      token,
      deadline,
    );
    const dead = (accounts?.accounts ?? []).filter(
      (account) => account.connection === connection,
    );
    for (const account of dead) {
      await myAccountRequest(
        `${apiBase}v1/connected-accounts/accounts/${encodeURIComponent(account.id)}`,
        token,
        deadline,
        "DELETE",
      );
    }
    recordSeedStep("unlinked", {
      reason: dead.length > 0 ? "removed" : "none",
    });
    uiLogger.info("Removed the dead Token Vault link before linking again", {
      "vault.connection": connection,
      "vault.unlink.count": dead.length,
    });
  } catch (error) {
    recordSeedStep("unlink_refused", {
      reason:
        error instanceof MyAccountRequestError
          ? String(error.status)
          : "unknown",
    });
    uiLogger.warn(
      "Could not remove the dead Token Vault link; linking anyway",
      {
        "error.message": String(error),
        "vault.connection": connection,
      },
    );
  }
}

/**
 * The My Account API lives under the tenant's own domain. The issuer setting is
 * what the provider gets as `domain`, a bare host in production, and the SDK
 * prepends the scheme the same way.
 */
function myAccountApiUrl(): string {
  const issuer = envs.get("SUPERBLOCKS_UI_AUTH_ISSUER_URL") ?? "";
  const base = /^https?:\/\//.test(issuer) ? issuer : `https://${issuer}`;
  return new URL("/me/", base).href;
}

class MyAccountRequestError extends Error {
  constructor(public readonly status: number) {
    super(`The My Account API answered ${status}`);
  }
}

async function myAccountRequest<T = unknown>(
  url: string,
  token: string,
  deadline: number,
  method: "GET" | "DELETE" = "GET",
): Promise<T | undefined> {
  const response = await fetch(url, {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    method,
    signal: AbortSignal.timeout(Math.max(deadline - Date.now(), 1)),
  });
  if (!response.ok) {
    throw new MyAccountRequestError(response.status);
  }
  return response.status === 204 ? undefined : ((await response.json()) as T);
}

/**
 * The OAuth error code the Auth0 SDK hangs off a rejection, which names the cause
 * where the message alone does not: a silent authorize that cannot use the
 * session fails as `login_required`, and a browser withholding the session cookie
 * from the iframe looks exactly the same. Without this field the only way to tell
 * why seeding never started is to read the Auth0 tenant log.
 */
function readOauthCode(error: unknown): string | undefined {
  return error instanceof GenericError ? error.error : undefined;
}

/**
 * Auth0's status is from a fixed set, so it is safe as a metric label. Its
 * problem-details text is not, and goes to the log instead. Any other failure is
 * reduced to one bucket rather than risking free text.
 */
function readMyAccountFailure(error: unknown): string {
  if (error instanceof MyAccountApiError) {
    return String(error.status);
  }
  return error instanceof HandshakeTimeout ? "timeout" : "unknown";
}

/**
 * Why the callback did not end in a link. Auth0's status when it took the code
 * and would not finish; the SDK's own code (`state_mismatch`,
 * `missing_transaction`, or an authorize error minting the My Account token)
 * when it never asked; one bucket for anything else, which in practice is the
 * network.
 */
function readCompletionFailure(error: unknown): string {
  if (error instanceof MyAccountApiError) {
    return String(error.status);
  }
  return readOauthCode(error) ?? "unknown";
}

class HandshakeTimeout extends Error {
  constructor() {
    super("The Token Vault handshake timed out");
  }
}

function withTimeout<T>(work: Promise<T>, deadline: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    work,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new HandshakeTimeout()),
        Math.max(deadline - Date.now(), 1),
      );
    }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * The `appState` the SDK put on a `ConnectError`, typed `any` there and stored
 * as `null` when there was none. Nothing else the callback can throw carries one.
 */
function readAppState(error: unknown): Partial<TokenVaultSeedAppState> {
  const appState: unknown =
    error instanceof ConnectError ? error.appState : undefined;
  return typeof appState === "object" && appState !== null ? appState : {};
}

function takePendingAppState(): Partial<TokenVaultSeedAppState> {
  const stored = window.sessionStorage.getItem(PENDING_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);
  try {
    const parsed: unknown = stored === null ? null : JSON.parse(stored);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    // Not ours or not JSON; the user goes home rather than nowhere.
    return {};
  }
}

function sameOriginUrl(url: string | undefined): string {
  try {
    const parsed = new URL(url ?? "", window.location.origin);
    if (parsed.origin === window.location.origin) {
      return parsed.toString();
    }
  } catch {
    // fall through to the origin root
  }
  return `${window.location.origin}/`;
}
